import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { NavBarContent } from '@components/Navbar';
import { HeadTags, NavBarContentData } from '@/shared/types/home.types';
import { api, mimir, site, validatesResponse } from '@/services/api';
import { fetchMateriaFromDirectus } from '@/services/directusMateria';
import * as Sentry from '@sentry/nextjs';
import { TimeLine, WidgetType } from '@/types/types';
import { BreadcrumbItem } from '@/components/Layout/Breadcrumb';
import { AxiosResponse } from 'axios';
import { SchemaType } from '@/components/Internals/InternalSchema';
import { useState } from 'react';
import { GalleryI, ImageI, EmbedI, CitacaoI, AspasI, ProsAndConsI } from '@/components/Internals/CustomComponentComposer/interfaces';
import { ZoomPrices } from '@/components/Layout/DealsBox';
import PageDataContext from '@/contexts/pageData';
import useBreakpoint from '@/hooks/useBreakpoint';
import { AdMgidProps } from '@/components/Ads/AdMgid';
import Layout from '@/components/Layout';

const AdMgid = dynamic(() => import('@/components/Ads/AdMgid'), { ssr: false });
const NewsItem = dynamic(() => import('@/components/Internals/NewsItem'));

export type partHtml = GalleryI | ImageI | EmbedI | CitacaoI | AspasI | ProsAndConsI | GenericPartHtml;

export interface GenericPartHtml {
  tipo: 'adMobile' | 'adDesktop' | string;
  valor: Array<string | partHtml>;
  tag?: keyof JSX.IntrinsicElements;
}

// TIPO_NOTICIAS = 1;
// TIPO_ANALISES = 2;
// TIPO_DICAS = 3;
// TIPO_TUTORIAIS = 4;
// TIPO_O_QUE_E = 5;
// TIPO_MATERIAS = 6;
// TIPO_COLUNAS = 7;
// TIPO_ARTIGOS = 8;
// TIPO_EDITORIAIS = 10;
//  TIPO_LISTAS = 11;
type PublicationTypes = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '10' | '11';

export type ArticleType = {
  id: string;
  timeline: number;
  cu: number;
  share: string;
  datalayer: string;
  videodestaque: string;
  patrocinado: string;
  conteudo: partHtml[];
  widget: WidgetType;
  blocoBlackFriday: boolean;
  botoes?: any;
  tipoPublicacao?: PublicationTypes;
};

export type FeaturedNewsApi = {
  data: any;
  status?: number;
  ssr: HeadTags[];
  segmentacoes?: [];
  headers: {
    'Cache-control': string;
    Expires: string;
  };
  breadcrumb?: BreadcrumbItem[];
  paginacao?: string;
  schema?: SchemaType;
};

/**
 * Parceiro da editoria à qual a categoria da matéria pertence. Vem do Directus
 * na resposta de matéria (`data.parceiros`, via `categoria_id.editoria_id.parceiros`),
 * já filtrado por status ativo e ordenado. Editorias legadas (ctup/eletro/ctauto)
 * não usam este campo — o patrocínio delas segue no banner hardcoded.
 */
export type EditorialPartner = {
  id: number;
  nome: string;
  link: string | null;
  logo: { id: string; url: string } | null;
};

/**
 * Dados da editoria (nova) para o banner de parceiro da interna — espelha o
 * banner legado (caixa com cor de marca + logo da editoria). Vem do Directus em
 * `data.editoria`; `null` quando a categoria não pertence a uma editoria.
 */
export type EditorialBanner = {
  slug: string;
  nome: string;
  cor: string | null;
  logo: { id: string; url: string } | null;
};

export type NewsDataType = {
  dominio: string;
  flags: number;
  titulo: string;
  tituloAlt: string;
  cu: string;
  oferta: boolean;
  publieditorial: boolean;
  article: ArticleType;
  fonte?: string;
  tipo?: string;
  categorias: string[];
  categoriaPrincipal: string;
  editoria?: EditorialBanner | null;
  parceiros?: EditorialPartner[];
  analises?: TimeLine[];
  ranking?: TimeLine[];
  moreTimelines?: TimeLine[];
  breadcrumb?: BreadcrumbItem[];
  segmentacoes?: [];
  maisAssistidos?: TimeLine[];
  ultimosPodcasts?: TimeLine[];
  url: string;
  paginacao?: string;
  capa: {
    blurhash: string;
    creditos: string;
    /**
     * Texto alternativo da imagem (a11y). Presente em matérias do Directus
     * (curado pelo editor em `imagem_capa_alt` ou auto-importado da YouTube
     * Data API); ausente em matérias do monolito legado, que caem no
     * fallback `creditos` no consumidor.
     */
    alt?: string;
    srcset: { [key: number]: string };
    url: string;
    dimensao: {
      largura: number;
      altura: number;
    };
  };
  publicacao: {
    autor: {
      nome: string;
      link: string;
    };
    editor: {
      nome: string;
      link: string;
    };
    data: string;
    dataPublicacao: string;
    dataAtualizacao: string;
  };
  precos?: ZoomPrices[];
};

export interface NewsType {
  navBarData: NavBarContent;
  responseData: {
    data: NewsDataType;
    segmentacoes: segmentacao[];
    breadcrumb: BreadcrumbItem[];
    ssr: HeadTags[];
    paginacao?: string;
    schema: SchemaType;
  };
}

type segmentacao = {
  chave: string;
  valor: string | [];
};

export default function News({ navBarData, responseData }: NewsType): JSX.Element {
  const [pageData, setPageData] = useState(responseData);

  const { data, ssr, schema } = responseData;
  const { segmentacoes, tipo, flags } = data;

  const isAdvertorial = tipo === 'artigo' && (flags & 2) !== 0;

  const [isTabletOrMobile] = useBreakpoint('tablet');

  const typeMgid: AdMgidProps['type'] = `${isTabletOrMobile ? 'mobile' : 'desktop'}`;

  const dataLayer = {
    category: data?.categoriaPrincipal || null,
    author: data?.publicacao?.autor?.nome || null,
    editor: data?.publicacao?.editor?.nome || null,
    contentType: data?.tipo || null,
  };

  return (
    <PageDataContext.Provider value={{ pageData, setPageData }}>
      <Layout
        layoutData={{
          segmentacoes,
          tagsData: ssr,
          navBarData,
          schema,
          contentType: data.tipo,
          breadcrumbList: data.breadcrumb,
        }}
        isAdvertorial={isAdvertorial}
        dataLayer={dataLayer}
      >
        <NewsItem newsData={data} type={tipo} isCurrentPage />
        {!isAdvertorial && <AdMgid type={typeMgid} />}
      </Layout>
    </PageDataContext.Provider>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { categorias, news = [] } = context.query;
  const { host, referer } = context.req.headers;
  let treatedResponse;
  let data = null;
  let dataMimir = [];
  let statusCode = 0;
  const pathName = `/${categorias}/${news[0]}/`;

  // Menu e conteudo sao independentes — dispara menu em paralelo
  const responseMenuReq = api.get<NavBarContentData>(`/componentes/menu/`);
  const promiseData: {
    homeReq: AxiosResponse<FeaturedNewsApi>;
    responseMenuReq: AxiosResponse<NavBarContentData>;
  } = {
    homeReq: null,
    responseMenuReq: null,
  };

  // 1. Tenta buscar no Directus primeiro
  let foundInDirectus = false;
  try {
    const directusResponse = await fetchMateriaFromDirectus(categorias, news[0]);
    const directusStatus = directusResponse?.data?.status;
    // 0 (sucesso) e 6/7 (redirect de slug alterado) são terminais: validatesResponse
    // converte 6/7 em `data.redirect` e a URL antiga responde 301. Sem 6/7 aqui o
    // envelope caía no fallback do CMS antigo, que não conhece a matéria Lisa e
    // devolvia 404 no lugar do 301 canônico. Status 3/erro seguem pro fallback.
    if (directusResponse && (directusStatus === 0 || directusStatus === 6 || directusStatus === 7)) {
      promiseData.homeReq = directusResponse;
      treatedResponse = validatesResponse(directusResponse, false, false, '', host, referer, pathName);
      data = treatedResponse.data;
      statusCode = treatedResponse.statusCode;
      foundInDirectus = true;
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { source: 'directus-materia' },
      extra: { categorias, slug: news[0] },
    });
  }

  // 2. Fallback: CMS antigo
  if (!foundInDirectus) {
    try {
      const siteResponse = await site.get<FeaturedNewsApi>(pathName);
      promiseData.homeReq = siteResponse;
      treatedResponse = validatesResponse(siteResponse, false, false, '', host, referer, pathName);
      data = treatedResponse.data;
      statusCode = treatedResponse.statusCode;
    } catch (error) {
      treatedResponse = validatesResponse(error, true, false, '', host, referer, pathName);
      data = treatedResponse.data;
      statusCode = treatedResponse.statusCode;
    }
  }

  // Aguarda menu
  try {
    const menuResponse = await responseMenuReq;
    promiseData.responseMenuReq = menuResponse;
  } catch {
    // Menu falhou — segue sem ele
  }

  // Catch-all [...news] aceita N segmentos depois da categoria. Paths como
  // /empresa/<marca>/produtos/<hash> ou /equipe/<pessoa>/y/ caem aqui; o getSSP
  // refetcha apenas news[0] e o monolito devolve 200 com payload não-matéria
  // (ex.: página de empresa), o que crasha o NewsItem em capa.url (FAWKES-35C).
  // Só dispara quando `data.data` veio populado mas sem shape de matéria —
  // matéria removida (status 8 / 410) tem `data.data` undefined e segue pra
  // statusCode 410 abaixo sem virar 404.
  if (data?.data && (!data.data.titulo || !data.data.conteudo)) {
    data.notFound = true;
  }

  // getServerSideProps só pode devolver as chaves `props`/`redirect`/`notFound`.
  // Quando o notFound acima é setado em cima do payload de sucesso, `data` ainda
  // carrega status/data/ssr/segmentacoes/schema/headers/breadcrumb; devolvê-lo
  // cru vaza essas chaves e o Next.js lança "Additional keys were returned from
  // getServerSideProps" (FAWKES-38S). Retorna shapes limpos.
  if (data.redirect) return { redirect: data.redirect, props: {} };
  if (data.notFound) return { notFound: true };

  context.res.setHeader('Cache-Control', data.headers['Cache-control']);
  context.res.setHeader('Expires', data.headers.Expires);
  context.res.statusCode = statusCode;

  if (statusCode === 410) {
    context.res.end();
  }

  const isAdvertorial = data?.data?.tipo === 'artigo' && (data?.data?.flags & 2) !== 0;

  if (isAdvertorial) {
    await Promise.all([
      mimir.get<Pick<FeaturedNewsApi, 'data'>>(`/timelines/mais/lidas/1-dia`),
      mimir.get<Pick<FeaturedNewsApi, 'data'>>(`/timelines/mais/lidas/1-dia?p=2`),
    ])
      .then(response => {
        dataMimir = [...response[0]?.data?.data?.timeline?.itens, ...response[1]?.data?.data?.timeline?.itens?.slice(0, 6)];
      })
      .catch(err => {
        console.log(err);
      });

    if (data?.data) data.data.ranking = dataMimir;
  }

  const responseData = data;
  const navBarData = promiseData.responseMenuReq.data.data;

  return {
    props: {
      responseData,
      navBarData,
    },
  };
};
